import { useState } from 'react';

import type { ApplyDiscountInput, Booking, BookingGarment } from '@/api/bookings';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { formatAmount } from '@core/format/money';
import { formatPct, netRental } from '@core/bookings/discount';
import {
  checkDiscount,
  discountFormFor,
  discountRequest,
  liveGarments,
  removeDiscountRequest,
  type DiscountForm,
} from '@core/bookings/discountForm';
import { withReference } from '@core/units/referenceNumber';
import { GarmentDiscountModal } from './GarmentDiscountModal';

/**
 * Giving, changing or taking back the discount on a booking (spec Section 4).
 *
 * <p>
 * <strong>This is the first screen in the product that consults `Role.MaxDiscountPct`.</strong> The
 * ceiling has resolved correctly since the column landed — per branch, highest-role-wins, with
 * administrators at 100 — and nothing anywhere read it, because nothing priced anything. The figure
 * it bounds is `booking.discount.ceilingPct`, resolved server-side for *this* user against *this*
 * booking's branch, which is why it is read off the booking rather than off the session: a manager
 * at head office is a clerk at the second shop, and the till they are standing at decides.
 * </p>
 *
 * <p>
 * <strong>Two ways in, because shops say it both ways.</strong> "10% off" and "make it nine
 * thousand" are one concession, and the second is how anyone rounds to a note. Both are measured as
 * a percentage before the ceiling applies — otherwise a limit written as 5% is defeated by the field
 * beside it — and the panel says so out loud rather than silently rejecting a figure that looks
 * fine.
 * </p>
 *
 * <p>
 * <strong>Removing is always offered, whatever the ceiling.</strong> Somebody who may not grant 20%
 * must still be able to take a mistyped 20% back off; the server agrees and skips the ceiling check
 * on a withdrawal. A shop where undoing a slip needs a manager is a shop where the slip stays.
 * </p>
 */
export function DiscountDrawer({
  booking,
  currency = '',
  busy = false,
  onClose,
  onApply,
}: {
  booking: Booking;
  currency?: string;
  busy?: boolean;
  onClose: () => void;
  onApply: (input: ApplyDiscountInput) => void;
}) {
  const { discount } = booking;

  // The form and its rules are shared with the phone (`@core/bookings/discountForm`): seeded from
  // what is on the booking, every live line held as a whole map because that is what the endpoint
  // takes, and the ceiling measured on both levels together.
  const [form, setForm] = useState<DiscountForm>(() => discountFormFor(booking));
  const { mode, pct, amount, reason, lines } = form;
  const patch = (next: Partial<DiscountForm>) => setForm((was) => ({ ...was, ...next }));

  // Which line's own concession is open in the modal.
  const [editingLine, setEditingLine] = useState<BookingGarment | null>(null);

  useEscapeKey(onClose);

  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const gross = booking.rentalAmount ?? 0;
  const garments = liveGarments(booking);

  const { resolved, over, negative, givingSomething, problem } = checkDiscount(booking, form);

  const lineAmountOf = (garmentId: number) =>
    resolved.lines.find((line) => line.key === garmentId)?.amount ?? 0;

  const valid = problem === null;

  const cash = (figure: number) => `${currency}${formatAmount(figure)}`;

  return (
    <div data-shortcut-layer className="fixed inset-0 z-9999 flex print:hidden">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 cursor-default bg-[var(--overlay-scrim)]"
      />

      {/* Hand-rolled rather than a `FormDrawer`, so the Enter handler goes on the `<aside>` and
          reaches every control inside by bubbling. The commit button is marked accordingly. */}
      <aside
        {...enterKeyScope}
        className="flex w-full max-w-[440px] flex-col border-s border-[var(--overlay-border)] bg-[var(--overlay-surface)]"
      >
        <header className="border-border-color flex items-center justify-between border-b px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {discount.amount ? 'Change the discount' : 'Give a discount'}
            </p>
            <p className="text-default text-[12px]">
              {booking.documentNumber || booking.quotationNumber || 'Unnumbered booking'} ·{' '}
              {booking.customerName}
            </p>
          </div>
          <Button icon="icon icon-x" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </header>

        <div ref={fields} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <div className="border-border-color grid grid-cols-3 gap-1 rounded border bg-light p-3">
            <Figure label="Rental" value={cash(gross)} />
            <Figure label="Discount" value={cash(resolved.total)} tone="brand" />
            <Figure label="Payable" value={cash(netRental(gross, resolved.total))} />
          </div>

          {discount.ceilingPct <= 0 && (
            <Notice tone="warning" title="Your role allows no discount">
              You can still remove one that is already on this booking — taking a concession back is
              deliberately not bounded by the limit that grants one.
            </Notice>
          )}

          <Field label="Give it as">
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ['pct', 'A percentage'],
                  ['amount', 'A fixed amount'],
                ] as const
              ).map(([option, label]) => (
                <button
                  key={option}
                  type="button"
                  disabled={busy}
                  onClick={() => patch({ mode: option })}
                  className={`h-8 cursor-pointer rounded border px-3 text-[12.5px] ${
                    mode === option
                      ? 'border-primary bg-primary text-white'
                      : 'border-border-color bg-white text-gray-900 hover:bg-light'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>

          {mode === 'pct' ? (
            <Field
              label="Percentage off"
              hint={
                discount.ceilingPct > 0
                  ? `Your role allows up to ${formatPct(discount.ceilingPct)}%.`
                  : undefined
              }
            >
              <input
                type="number"
                onWheel={(event) => event.currentTarget.blur()}
                min={0}
                max={100}
                step="0.01"
                disabled={busy}
                value={pct}
                onChange={(event) => patch({ pct: event.target.value })}
                className={controlClass}
              />
            </Field>
          ) : (
            <Field
              label="Amount off"
              hint="Measured back as a percentage before the same limit applies — a fixed sum is not a way round it."
            >
              <input
                type="number"
                onWheel={(event) => event.currentTarget.blur()}
                min={0}
                step="0.01"
                disabled={busy}
                value={amount}
                onChange={(event) => patch({ amount: event.target.value })}
                className={controlClass}
              />
            </Field>
          )}

          {resolved.total > 0 && (
            <p className="text-default text-[12px]">
              That is <b className="text-gray-900">{formatPct(resolved.effectivePct)}%</b> off{' '}
              {cash(gross)} in total
              {resolved.lineTotal > 0 && resolved.bookingAmount > 0
                ? ` — ${cash(resolved.lineTotal)} on the garments and ${cash(
                    resolved.bookingAmount,
                  )} on the booking.`
                : '.'}
            </p>
          )}

          {over && (
            <Notice tone="danger" title="Over your limit">
              That is {formatPct(resolved.effectivePct)}% off in total, and your role allows{' '}
              {formatPct(discount.ceilingPct)}%. The limit is on the whole concession, so moving it
              between the garments and the booking will not get past it — reduce it, or ask someone
              with a higher limit.
            </Notice>
          )}

          {negative && (
            <Notice tone="danger" title="A discount cannot be negative">
              To charge more than the card says, change the rate card.
            </Notice>
          )}

          {givingSomething && (
            <Field
              label="Why it was given"
              hint="Required. A concession with no sentence beside it is the line nobody can account for a month later."
            >
              <input
                value={reason}
                disabled={busy}
                onChange={(event) => patch({ reason: event.target.value })}
                placeholder="Repeat customer, third wedding this year"
                className={controlClass}
              />
            </Field>
          )}

          {/* Per garment, because "take ₹500 off that sherwani" is a fact about a garment and
              typing it as a whole-booking figure loses which one it was about — the question a
              customer asks when they come back next year expecting the same price on the same suit. */}
          {garments.length > 0 && (
            <div>
              <p className="mb-1.5 text-[12.5px] font-semibold text-gray-900">Per garment</p>

              <ul className="flex flex-col gap-1">
                {garments.map((garment) => {
                  const off = lineAmountOf(garment.id);
                  const rate = garment.rentalRate ?? 0;

                  return (
                    <li
                      key={garment.id}
                      className="border-border-color flex items-center gap-2 rounded border px-2.5 py-1.5"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] text-gray-900">
                          {garment.itemName}
                        </span>
                        <span className="text-default font-mono-nums block text-[11px]">
                          {withReference(garment.serialCode, garment.referenceNumber)}
                        </span>
                      </span>

                      <span className="text-end">
                        <span
                          className={`font-mono-nums block text-[12.5px] ${
                            off > 0 ? 'text-default line-through' : 'text-gray-900'
                          }`}
                        >
                          {cash(rate)}
                        </span>
                        {off > 0 && (
                          <span className="font-mono-nums text-primary block text-[12.5px]">
                            {cash(Math.max(0, rate - off))}
                          </span>
                        )}
                      </span>

                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setEditingLine(garment)}
                        aria-label={`Discount ${garment.serialCode}`}
                        className={`flex size-6 cursor-pointer items-center justify-center rounded disabled:cursor-not-allowed disabled:opacity-50 ${
                          off > 0
                            ? 'text-primary bg-primary-transparent'
                            : 'text-default hover:bg-light'
                        }`}
                      >
                        <i className="icon icon-percent text-[12px]" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* The deposit deliberately does not move. Security is against the garment coming back,
              and a price concession has not made the garment cheaper — so a discounted booking is
              secured exactly as far as an undiscounted one. Said here because a clerk watching the
              payable figure fall would otherwise expect the deposit to follow it. */}
          <p className="text-default text-[11.5px] leading-relaxed">
            The deposit is unchanged — it secures the garments, not the price. Billable alteration
            is not discounted either: the tailor is paid whatever the customer is charged.
          </p>
        </div>

        <footer className="border-border-color flex items-center gap-2 border-t bg-white px-5 py-3">
          {/* Clears both levels at once, which is what "remove the discount" means to whoever
              presses it. Unbounded by the ceiling on the server, deliberately. */}
          {(discount.totalAmount > 0 || resolved.total > 0) && (
            <Button variant="danger" disabled={busy} onClick={() => onApply(removeDiscountRequest)}>
              Remove
            </Button>
          )}

          <span className="me-auto" />

          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          <Button
            variant="primary"
            data-enter-key-commit
            shortcut="save"
            disabled={!valid || busy}
            onClick={() => onApply(discountRequest(booking, form))}
          >
            Apply {resolved.total > 0 ? cash(resolved.total) : ''}
          </Button>
        </footer>
      </aside>

      {editingLine && (
        <GarmentDiscountModal
          name={editingLine.itemName}
          serialCode={editingLine.serialCode}
          rate={editingLine.rentalRate ?? 0}
          initial={lines[editingLine.id] ?? { pct: null, amount: null }}
          ceilingPct={discount.ceilingPct}
          currency={currency}
          busy={busy}
          onClose={() => setEditingLine(null)}
          onApply={(line) => {
            patch({ lines: { ...lines, [editingLine.id]: line } });
            setEditingLine(null);
          }}
        />
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'brand' | undefined;
}) {
  return (
    <div>
      <p className="text-default text-[11px] uppercase">{label}</p>
      <p
        className={`font-mono-nums text-[13.5px] font-semibold ${
          tone === 'brand' ? 'text-primary' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
